import React, { useState, useEffect, useRef, useMemo } from "react";
import rest from "../../rest";
import { Popper, Badge, useTheme, useMediaQuery } from "@material-ui/core"; //Backdrop
import ChatIcon from "@material-ui/icons/Chat";
import IconButtonStyled from "../general/IconButtonStyled";
import { useSelector } from "react-redux";
import "./chat.css";
import { MySnackbar } from "../mySnackbar/MySnackbar";
import { fetchChatData, fetchUsers, groupMessagesByDate } from "./function";
import ChatFullView from "./ChatFullView";
import ChatSmallView from "./ChatSmallView";
import ChatHistoriqueView from "./ChatHistoriqueView";

const ChatPopper = ({ nbMessage = 0, setReloadNbMsg, reloadNbMsg }) => {
  const [anchorEl, setAnchorEl] = useState(false);
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [snack, setSnack] = useState({});
  const [newMessage, setNewMessage] = useState("");
  const [selectedUser, setSelectedUser] = useState(0);
  const [users, setUsers] = useState(null);
  const [messageHistory, setMessageHistory] = useState([]);
  const [chatViewMode, setViewMode] = useState("small");
  const [loadingMessages, setLoadingMessages] = useState(true); // State pour indiquer si les messages sont en cours de chargement
  const [groupedMessages, setGroupedMessages] = useState([]);

  const [usersHistory, setUsersHistory] = useState([]);
  const [userSelectedHistoy, setUserSelectedHistoy] = useState(0);
  const [filterMessageHistory, setfilterMessageHistory] = useState([]);

  const open = Boolean(anchorEl);
  const id = open ? "chat-popper" : undefined;
  const previousSelectedUserRef = useRef(null);
  const timeBeforCheckNewMgs = 10000; // 1000 = 1s
  const timeBeforNotRead = 10000; // 1000 = 1s
  const currentUser = useSelector((state) => state.user);

  const theme = useTheme();
  const querysmDown = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    async function fetchData() {
      const users = await fetchUsers();
      setUsers(users);
      updateNbNotRead();
      if (users.length <= 0) {
        setViewMode('history');
      }
    }
    fetchData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (open) {
      const messageFrom = messages?.filter(
        (msg) =>
          msg.Sender_Person?.ID === selectedUser ||
          msg.Reciver_Person?.ID === selectedUser
      );
      setFilteredMessages(messageFrom);
      setGroupedMessages(groupMessagesByDate(messageFrom));
      previousSelectedUserRef.current = selectedUser;
    }
    // eslint-disable-next-line
  }, [selectedUser]);

  function updateNbNotRead() {
    if (
      previousSelectedUserRef.current !== null &&
      previousSelectedUserRef.current === selectedUser
    ) {
      setUsers((prevState) => {
        return prevState.map((user) => {
          if (user.ID === selectedUser) {
            return { ...user, nbNotRead: 0 };
          }
          return user;
        });
      });
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      updateNbNotRead();
    }, timeBeforNotRead);
    if (open) {
      setLoadingMessages(true);
      fetchChatData(
        setMessages,
        setFilteredMessages,
        setGroupedMessages,
        setUsers,
        setSelectedUser,
        currentUser,
        messages,
        selectedUser,
        setLoadingMessages,
        setMessageHistory,
        setUsersHistory
      );
    }
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [open, currentUser, selectedUser]);

  useEffect(() => {
    if (open) {
      // setLoadingMessages(true);
      fetchChatData(
        setMessages,
        setFilteredMessages,
        setGroupedMessages,
        setUsers,
        setSelectedUser,
        currentUser,
        messages,
        selectedUser,
        setLoadingMessages,
        setMessageHistory,
        setUsersHistory
      );
    }
    // eslint-disable-next-line
  }, [reloadNbMsg]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (open) {
        fetchChatData(
          setMessages,
          setFilteredMessages,
          setGroupedMessages,
          setUsers,
          setSelectedUser,
          currentUser,
          messages,
          selectedUser,
          setLoadingMessages,
          setMessageHistory,
          setUsersHistory
        );
      }
    }, timeBeforCheckNewMgs);
    // Clean up the interval upon component unmounting
    return () => clearInterval(timer);
    // Empty dependency array ensures execution only once at startup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedUser]);

  const handleSendMessage = () => {
    if (newMessage === "") {
      setSnack({
        message: "Ajoutez un message",
        show: true,
        severity: "error",
      });
      return;
    }
    if (selectedUser) {
      const messageData = {
        text: newMessage,
        recipient: selectedUser,
      };

      rest
        .post("/benevols/chat/sendMessage.json", messageData)
        .then((response) => {
          // setMessages([...messages, response.data.message]);
          setNewMessage("");
          if (setReloadNbMsg) setReloadNbMsg((r) => !r);
        })
        .catch((error) => {
          console.error("Error sending message", error);
        });
    } else {
      setSnack({
        message: "Spécifiez un destinataire",
        show: true,
        severity: "error",
      });
    }
  };

  const handleClick = (event) => {
    // open chat
    setAnchorEl(anchorEl ? null : event.currentTarget);
    if (setReloadNbMsg) setReloadNbMsg((r) => !r);
  };

  const handleSelectUser = (id) => {
    setSelectedUser(id);
  };

  const handleViewScreen = (screen) => {
    setViewMode(screen);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSendMessage();
    }
  };

  const handleSelectUserHistory = (id) => {
    setUserSelectedHistoy(id);
    setfilterMessageHistory(
      messageHistory.filter(
        (msg) => msg.Reciver_Person?.ID === id || msg.Sender_Person?.ID === id
      )
    );
  };

  const showHistory = useMemo(() => {
    return chatViewMode === "history" && messageHistory.length > 0;
  }, [messageHistory, chatViewMode]);

  return (
    <div>
      <MySnackbar params={snack} />
      <Badge
        badgeContent={nbMessage}
        color="secondary"
        invisible={nbMessage <= 0}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        style={{ position: "fixed", bottom: querysmDown ? '72px' : "28px", right: querysmDown ? '20px' : "70px" }} // Positionnement de la pastille
      ></Badge>
      <IconButtonStyled
        aria-describedby={id}
        variant="outlined"
        onClick={handleClick}
        style={{
          position: "fixed",
          bottom: querysmDown ? '52px' : "3px",
          right:  querysmDown ? '22px' : "70px",
          width: 26,
          height: 26,
          padding: 6,
          backgroundColor: "hsla(0, 100%, 100%, 1)",
        }}
        iconSize={16}
        tooltipTitle="Messagerie"
      >
        <ChatIcon />
      </IconButtonStyled>
      <Popper id={id} open={open} anchorEl={anchorEl} style={{ zIndex: 999 }}>
        {showHistory ? (
          <ChatHistoriqueView
            users={usersHistory}
            currentUser={currentUser}
            groupedMessages={groupMessagesByDate(filterMessageHistory)}
            filteredMessages={filterMessageHistory}
            loadingMessages={loadingMessages}
            userSelectedHistoy={userSelectedHistoy}
            handleSelectUserHistory={handleSelectUserHistory}
            handleViewScreen={handleViewScreen}
            showChat={users?.length > 0} // Indiquer s'il y a des utilisateurs dans le chat
          />
        ) : (
          users &&
          users.length > 0 &&
          (chatViewMode === "full" ? (
            <ChatFullView
              users={users}
              selectedUser={selectedUser}
              handleSelectUser={handleSelectUser}
              handleViewScreen={handleViewScreen}
              currentUser={currentUser}
              groupedMessages={groupedMessages}
              filteredMessages={filteredMessages}
              loadingMessages={loadingMessages}
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              handleKeyDown={handleKeyDown}
              handleSendMessage={handleSendMessage}
              showHistory={usersHistory?.length > 0} // Indiquer s'il y a un historique à afficher
            />
          ) : (
            <ChatSmallView
              users={users}
              selectedUser={selectedUser}
              handleSelectUser={handleSelectUser}
              handleViewScreen={handleViewScreen}
              currentUser={currentUser}
              groupedMessages={groupedMessages}
              filteredMessages={filteredMessages}
              loadingMessages={loadingMessages}
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              handleKeyDown={handleKeyDown}
              handleSendMessage={handleSendMessage}
            />
          ))
        )}
      </Popper>
    </div>
  );
};

export default ChatPopper;
